/**
 * Google Analytics 4 wrapper.
 *
 * TEST BUILD ONLY — index.html loads gtag.js. Before public launch:
 *   1. Set ENABLED to false (instant kill: all call sites become no-ops), OR
 *   2. Delete this folder + the gtag snippet in index.html. TypeScript will
 *      then flag every import site for removal.
 *
 * Design goals:
 *   - One module, one import path: `import { track, trackPageview, ... } from '../analytics/ga.js'`
 *   - No-op safe: every export tolerates gtag missing, blocked, or disabled.
 *   - Privacy: callers pass only primitives/enums. No free text, names, or notes.
 */

const ENABLED = true;
const MEASUREMENT_ID = 'G-1W8X91NT41';
// Flip to true to mirror every event to console.debug for local debugging.
const DEV_LOG = false;
// While true, every event also carries `debug_mode: true` so it appears in
// GA4 DebugView in real time. Turn off before launch if launch keeps GA.
const DEBUG_MODE = true;

type ParamVal = string | number | boolean;
export type Params = Record<string, ParamVal>;

type GtagArgs =
  | ['js', Date]
  | ['config', string, Record<string, unknown>?]
  | ['event', string, Record<string, unknown>?]
  | ['set', Record<string, unknown>];

declare global {
  interface Window {
    gtag?: (...args: GtagArgs) => void;
    dataLayer?: unknown[];
  }
}

let scriptLoaded = false;

function pageTitleFor(path: string): string {
  if (!path || path === '/') return 'today';
  const head = path.replace(/^\/+/, '').split(/[/?]/)[0];
  return head || 'today';
}

function currentRouteContext(): { page_path: string; page_title: string; page_location: string } {
  const hash = location.hash.replace(/^#/, '') || '/today';
  const path = (hash.split('?')[0] ?? '/today') || '/today';
  return {
    page_path: path,
    page_title: pageTitleFor(path),
    page_location: `${location.origin}${location.pathname}#${path}`,
  };
}

/**
 * Inject the async gtag.js script and fire the initial `config` call.
 * Idempotent — safe to call multiple times. Call only after user consent.
 *
 * IMPORTANT: We `set` the route context BEFORE firing `config`. GA4's
 * Enhanced Measurement (enabled by default) auto-fires a page_view at init
 * regardless of `send_page_view`. Setting defaults first makes that auto
 * page_view carry the correct hash route instead of the bare pathname `/`.
 */
export function loadAnalytics(): void {
  if (!ENABLED) return;
  if (scriptLoaded) return;
  scriptLoaded = true;
  try {
    const ctx = currentRouteContext();
    // Defaults applied to every subsequent event, including EM auto page_view.
    gtagSafe('set', ctx);
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
    s.onerror = () => { /* network blocked — events buffer in dataLayer harmlessly */ };
    document.head.appendChild(s);
    gtagSafe('config', MEASUREMENT_ID, {
      page_path: ctx.page_path,
      page_title: ctx.page_title,
      page_location: ctx.page_location,
      ...(DEBUG_MODE ? { debug_mode: true } : {}),
    });
  } catch {
    /* analytics must never break the app */
  }
}

function gtagSafe(...args: GtagArgs): void {
  if (!ENABLED) return;
  const fn = window.gtag;
  if (typeof fn !== 'function') return;
  try {
    fn(...args);
  } catch {
    /* analytics must never break the app */
  }
}

function devLog(name: string, params?: Record<string, unknown>): void {
  if (!DEV_LOG) return;
  // eslint-disable-next-line no-console
  console.debug('[analytics]', name, params ?? {});
}

function withDebug(params: Record<string, unknown>): Record<string, unknown> {
  return DEBUG_MODE ? { ...params, debug_mode: true } : params;
}

/** Custom event. Params should be primitives only. */
export function track(name: string, params?: Params): void {
  devLog(name, params);
  gtagSafe('event', name, withDebug(params ?? {}));
}

let lastPageviewPath: string | null = null;

/** SPA pageview for the current hash route. Dedupes consecutive duplicates. */
export function trackPageview(path: string): void {
  if (path === lastPageviewPath) return;
  lastPageviewPath = path;
  const page_title = pageTitleFor(path);
  const page_location = `${location.origin}${location.pathname}#${path}`;
  const ctx = { page_path: path, page_title, page_location };
  // Update defaults so subsequent custom events carry the new route context.
  gtagSafe('set', ctx);
  devLog('page_view', ctx);
  gtagSafe('event', 'page_view', withDebug(ctx));
  // Also reflect the route in the browser tab title so the user (and any
  // crash report tooling) sees which screen they were on.
  try { document.title = `Wean — ${page_title}`; } catch { /* ignore */ }
}

/** Friendly screen-name event (orthogonal to pageview; nicer in GA reports). */
export function trackScreen(name: string): void {
  track('screen_view', { screen_name: name });
}

/** Timing event in milliseconds (rounded to int). */
export function trackTiming(name: string, ms: number, extra?: Params): void {
  track(name, { duration_ms: Math.round(ms), ...(extra ?? {}) });
}

/**
 * Error event. Captures error name + truncated message only — no stack,
 * no DOM context, no user content.
 */
export function trackError(scope: string, err: unknown): void {
  const name = err instanceof Error ? err.name : typeof err;
  const message = err instanceof Error
    ? err.message.slice(0, 200)
    : String(err).slice(0, 200);
  track('app_error', { scope, error_name: name, error_message: message });
}

/** Set a user property (low-cardinality enum only). */
export function setUserProp(key: string, value: ParamVal): void {
  devLog('user_property', { [key]: value });
  gtagSafe('set', { [key]: value });
}
